import ItemLoader from '@app/Components/ItemLoader';
import FormAlertInterface from '@app/Interface/FormAlertInterface';
import UserModel from '@app/Model/UserModel';
import UserAuthentication from '@app/Security/UserAuthentication';
import ValidatedType from '@app/Type/ValidatedType';
import TypeAssertions from '@app/utils/TypeAssertions';
import { ActionGroup, Alert, Button, Form, FormAlert, FormGroup, FormHelperText, TextInput } from '@patternfly/react-core';
import { ExclamationCircleIcon } from '@patternfly/react-icons';
import * as React from 'react';
import PasswordStrengthBar, { PasswordFeedback } from 'react-password-strength-bar';
import { Link } from 'react-router-dom';

interface ResetPasswordProps {
    jwt: string,
    userId: number
}

interface PasswordScoreInterface {
    score: number;
    feedback: PasswordFeedback;
}

const ResetPassword: React.FC<ResetPasswordProps> = ({ jwt, userId }) => {

    const [validJwt, setValidJwt] = React.useState(false);

    const [isLoading, setIsLoading] = React.useState(true);

    const [password, setPassword] = React.useState("");

    const [passwordValid, setPasswordValid] = React.useState<ValidatedType>("default");

    const [confirmPassword, setConfirmPassword] = React.useState("");

    const [confirmPasswordValid, setConfirmPasswordValid] = React.useState<ValidatedType>("default");

    const [formValidated, setFormValidated] = React.useState<ValidatedType>("default");

    const [formAlertMessage, setFormAlertMessage] = React.useState<FormAlertInterface>({ variant: "default", title: "", isInline: true });

    const [isFormProcessing, setIsFormProcessing] = React.useState(false);

    const initialPasswordStrength: PasswordScoreInterface = { score: 0, feedback: {} };

    const [passwordStrength, setPasswordStrength] = React.useState(initialPasswordStrength);

    React.useEffect(() => {

        const controller = new AbortController();

        let mounted = true;

        async function validateUserJwt() {

            const valid = await UserAuthentication.validateJwt(controller, userId, jwt);

            if (mounted) {
                setValidJwt(valid);

                setIsLoading(false);
            }

        }

        setIsLoading(true);

        validateUserJwt();

        return () => {
            mounted = false;
            controller?.abort();
        };

    }, [userId, jwt]);

    const validForm = (): boolean => {

        let isValid = true;

        if (TypeAssertions.isStringNullOrEmpty(password) || password.length < 6) {

            isValid = false;

            setPasswordValid("error");

        } else if (passwordStrength.score < 3) {

            isValid = false;

            setPasswordValid("error");

            const feedback = passwordStrength.feedback.suggestions?.map( (suggestion, index) => (
                <span key={index}>{suggestion}</span>   
            ));

            setFormAlertMessage({
                variant: "danger",
                title: passwordStrength.feedback.warning ? passwordStrength.feedback.warning : "Please enter a stronger password.",
                isInline: true,
                feedback: feedback
            });

            setFormValidated("error");

        } else if (password !== confirmPassword) {

            isValid = false;

            setConfirmPasswordValid("error");

        }

        return isValid;

    }

    const handleChangePasswordScore = (score: number, feedback: PasswordFeedback) => {

        const newScore: PasswordScoreInterface = {
            score: score,
            feedback: feedback
        };

        setPasswordStrength(newScore);

    }

    const handleSubmit = async () => {

        setIsFormProcessing(true);

        setPasswordValid("default");

        setConfirmPasswordValid("default");

        setFormValidated("default");

        // TODO: Determine how to check for valid email address
        if (validForm()) {

            const userToUpdate = new UserModel();

            // NOTE: to update password, we just need the user id. 
            userToUpdate.id = userId;

            const wasUpdated = await UserAuthentication.saveUserPassword(userToUpdate, password, jwt);

            if (wasUpdated) {

                setFormValidated("success");

                setFormAlertMessage({ variant: "success", title: "Your password was updated. You may try to login now.", isInline: true });

            } else {

                setFormValidated("error");

                setFormAlertMessage({ variant: "danger", title: "An error occured attempting to save the password. Pease try again.", isInline: true });

            }
        }

        setIsFormProcessing(false);

    };

    if (validJwt && formValidated === "success") {

        return (
            <Form>
                <FormAlert>
                    <Alert
                        variant={formAlertMessage.variant}
                        title={formAlertMessage.title}
                        aria-live="polite"
                        isInline={formAlertMessage.isInline}
                        role="alert"
                    >
                        <Link to="/">Login</Link>
                    </Alert>
                </FormAlert>
            </Form>
        );

    } else if (validJwt) {

        return (
            <Form>
                {formValidated !== "default" && (
                    <FormAlert>
                        <Alert
                            variant={formAlertMessage.variant}
                            title={formAlertMessage.title}
                            aria-live="polite"
                            isInline={formAlertMessage.isInline}
                            role="alert"
                        >{formAlertMessage.feedback}</Alert>
                    </FormAlert>
                )}
                <FormGroup
                    label="Password"
                    isRequired
                    helperText={
                        <FormHelperText icon={<ExclamationCircleIcon />} isHidden={passwordValid !== "error"}>Please enter a password.</FormHelperText>
                    }
                    helperTextInvalid="Enter a valid password"
                    helperTextInvalidIcon={<ExclamationCircleIcon />}
                    fieldId="form-email"
                    validated={passwordValid}
                >
                    <TextInput
                        type="password"
                        validated={passwordValid}
                        value={password}
                        id="password"
                        aria-describedby="form-email"
                        onChange={(newValue: string) => setPassword(newValue)}
                    />
                    <PasswordStrengthBar minLength={6} password={password} onChangeScore={handleChangePasswordScore} />
                </FormGroup>
                <FormGroup
                    label="Confirm Password"
                    isRequired
                    helperText={
                        <FormHelperText icon={<ExclamationCircleIcon />} isHidden={confirmPasswordValid !== "error"}>Password don&apos;t match.</FormHelperText>
                    }
                    helperTextInvalid="The passwords don't match."
                    helperTextInvalidIcon={<ExclamationCircleIcon />}
                    fieldId="form-confirm-password"
                    validated={confirmPasswordValid}
                >
                    <TextInput
                        type="password"
                        validated={confirmPasswordValid}
                        value={confirmPassword}
                        id="confirm-password"
                        aria-describedby="form-confirm-password"
                        onChange={(newValue: string) => setConfirmPassword(newValue)}
                    />
                </FormGroup>
                <ActionGroup>
                    <Button
                        variant="primary"
                        onClick={handleSubmit}
                        spinnerAriaValueText={isFormProcessing ? "Saving" : undefined}
                        isLoading={isFormProcessing}
                    >
                        {isFormProcessing ? "Saving" : "Submit"}
                    </Button>
                </ActionGroup>
            </Form>
        );

    } else {

        return (
            <FormAlert>
                {isLoading
                    ? <ItemLoader />
                    :
                    <Alert
                        variant="danger"
                        title="Invalid Token"
                        role="alert"
                    >
                        <p>The token was invalid. Please try clicking the link in your email again or use the forgot password link below.</p>
                    </Alert>
                }

            </FormAlert>
        );
    }



}

export default ResetPassword;